import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Pin, Lock, Eye, MessageCircle, MoreVertical, Edit2, Trash2 } from 'lucide-react';
import axios from 'axios';
import { useAuthStore } from '../stores/authStore';
import PostCard from '../components/discussions/PostCard';
import MentionTextarea from '../components/common/MentionTextarea';

export default function DiscussionThread() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuthStore();
  const [discussion, setDiscussion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [postContent, setPostContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [showMenu, setShowMenu] = useState(false);

  useEffect(() => {
    loadDiscussion();
  }, [id]);

  const loadDiscussion = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:8000'}/api/discussions/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDiscussion(response.data);
      setEditTitle(response.data.title);
      setEditContent(response.data.content);
    } catch (error) {
      console.error('Failed to load discussion:', error);
      setError(error.response?.data?.message || error.response?.status === 404 ? 'Discussion not found' : 'Failed to load discussion');
    } finally {
      setLoading(false);
    }
  };

  const handlePost = async () => {
    if (!postContent.trim()) return;

    try {
      await axios.post(
        `${import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:8000'}/api/discussions/${id}/posts`,
        { content: postContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPostContent('');
      loadDiscussion();
    } catch (error) {
      console.error('Failed to post:', error);
      alert(error.response?.data?.error || 'Failed to post. This discussion may be locked.');
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(
        `${import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:8000'}/api/discussions/${id}`,
        { title: editTitle, content: editContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setIsEditing(false);
      loadDiscussion();
    } catch (error) {
      console.error('Failed to update discussion:', error);
      alert('Failed to update discussion');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this discussion?')) return;

    try {
      await axios.delete(`${import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:8000'}/api/discussions/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      navigate('/discussions');
    } catch (error) {
      console.error('Failed to delete discussion:', error);
      alert('Failed to delete discussion');
    }
  };

  const handleTogglePin = async () => {
    try {
      await axios.post(
        `${import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:8000'}/api/discussions/${id}/pin`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadDiscussion();
    } catch (error) {
      console.error('Failed to toggle pin:', error);
    }
  };

  const handleToggleLock = async () => {
    try {
      await axios.post(
        `${import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:8000'}/api/discussions/${id}/lock`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadDiscussion();
    } catch (error) {
      console.error('Failed to toggle lock:', error);
    }
  };

  const handleToggleHide = async () => {
    try {
      await axios.post(
        `${import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:8000'}/api/discussions/${id}/hide`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadDiscussion();
    } catch (error) {
      console.error('Failed to toggle hide:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error || !discussion) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="text-red-600 mb-4">
            <svg className="h-16 w-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-xl font-medium text-gray-900 mb-2">
            {error || 'Discussion not found'}
          </h3>
          <p className="text-gray-600 mb-6">
            This discussion may have been deleted or you don't have permission to view it.
          </p>
          <button
            onClick={() => navigate('/discussions')}
            className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to discussions
          </button>
        </div>
      </div>
    );
  }

  const isOwner = user?.id === discussion.user_id;
  const isAdmin = user?.role === 'admin' || user?.role === 'instructor';

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft className="h-5 w-5" />
        Back
      </button>

      {/* Discussion header */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            {isEditing ? (
              <div className="space-y-3">
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-xl font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  rows={4}
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleUpdate}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2">
                  {discussion.is_pinned && (
                    <Pin className="h-5 w-5 text-indigo-600" />
                  )}
                  {discussion.is_locked && (
                    <Lock className="h-5 w-5 text-gray-400" />
                  )}
                  <h1 className="text-2xl font-bold text-gray-900">{discussion.title}</h1>
                </div>
                
                <p className="text-gray-700 whitespace-pre-wrap mb-4">{discussion.content}</p>

                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <MessageCircle className="h-4 w-4" />
                    <span>{discussion.posts_count} {discussion.posts_count === 1 ? 'reply' : 'replies'}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Eye className="h-4 w-4" />
                    <span>{discussion.views_count} views</span>
                  </div>
                </div>

                <div className="mt-4 flex items-center gap-2 pt-4 border-t border-gray-200">
                  <img
                    src={`https://ui-avatars.com/api/?name=${encodeURIComponent(discussion.user?.name || 'User')}&background=4F46E5&color=fff`}
                    alt={discussion.user?.name}
                    className="h-10 w-10 rounded-full"
                  />
                  <div>
                    <p className="font-medium text-gray-900">{discussion.user?.name}</p>
                    <p className="text-sm text-gray-500">Posted {formatDate(discussion.created_at)}</p>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Actions menu */}
          {(isOwner || isAdmin) && !isEditing && (
            <div className="relative ml-4">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-2 rounded hover:bg-gray-100"
              >
                <MoreVertical className="h-5 w-5 text-gray-400" />
              </button>
              {showMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                  {isOwner && (
                    <button
                      onClick={() => { setIsEditing(true); setShowMenu(false); }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <Edit2 className="h-4 w-4" />
                      Edit
                    </button>
                  )}
                  {isAdmin && (
                    <>
                      <button
                        onClick={() => { handleTogglePin(); setShowMenu(false); }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <Pin className="h-4 w-4" />
                        {discussion.is_pinned ? 'Unpin' : 'Pin'}
                      </button>
                      <button
                        onClick={() => { handleToggleLock(); setShowMenu(false); }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <Lock className="h-4 w-4" />
                        {discussion.is_locked ? 'Unlock' : 'Lock'}
                      </button>
                      <button
                        onClick={() => { handleToggleHide(); setShowMenu(false); }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <Eye className="h-4 w-4" />
                        {discussion.is_hidden ? 'Show' : 'Hide'}
                      </button>
                    </>
                  )}
                  {(isOwner || isAdmin) && (
                    <button
                      onClick={() => { handleDelete(); setShowMenu(false); }}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Reply form */}
      {!discussion.is_locked && (
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <h3 className="text-lg font-semibold mb-3">Add a reply</h3>
          <MentionTextarea
            value={postContent}
            onChange={(e) => setPostContent(e.target.value)}
            placeholder="Write your reply... Use @ to mention someone"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            rows={4}
          />
          <div className="mt-3 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              💡 Tip: Type <span className="font-mono bg-gray-100 px-1 rounded">@</span> to mention users
            </p>
            <button
              onClick={handlePost}
              disabled={!postContent.trim()}
              className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Post Reply
            </button>
          </div>
        </div>
      )}

      {discussion.is_locked && (
        <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-6 flex items-center gap-2">
          <Lock className="h-5 w-5 text-yellow-600" />
          <p className="text-yellow-800">This discussion is locked. No new replies can be posted.</p>
        </div>
      )}

      {/* Posts */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {discussion.posts_count} {discussion.posts_count === 1 ? 'Reply' : 'Replies'}
        </h3>
        
        {discussion.top_level_posts && discussion.top_level_posts.length > 0 ? (
          discussion.top_level_posts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              discussionOwnerId={discussion.user_id}
              onUpdate={loadDiscussion}
              onDelete={loadDiscussion}
            />
          ))
        ) : (
          <div className="text-center py-8 bg-white rounded-lg border border-gray-200">
            <MessageCircle className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-gray-500">No replies yet. Be the first to reply!</p>
          </div>
        )}
      </div>
    </div>
  );
}
